export type UserRole = "super_admin" | "admin" | "editor" | "author" | "columnist";

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar: string;
  isActive: boolean;
  lastLogin: string;
  articleCount: number;
}

export interface Subcategory {
  id: string;
  name: string;
  slug: string;
  parentId: string;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  color: string;
  imageUrl?: string;
  articleCount: number;
  isActive: boolean;
  order: number;
  showInMenu: boolean;
  showOnHomepage: boolean;
  homepageOrder: number;
  seoTitle?: string;
  seoDescription?: string;
  subcategories?: Subcategory[];
}

export interface MediaItem {
  id: string;
  name: string;
  url: string;
  type: "image" | "video";
  size: string;
  uploadedAt: string;
  dimensions?: string;
}

export type AdType = "adsense" | "custom_html" | "image_banner";
export type AdDevice = "desktop" | "tablet" | "mobile" | "all";
export type AdPageType = "homepage" | "article" | "category" | "search" | "all";
export type AdPosition =
  | "header_banner"
  | "top_leaderboard"
  | "sidebar_top"
  | "sidebar_large"
  | "in_article_p2"
  | "in_article_p5"
  | "homepage_section"
  | "category_page"
  | "article_bottom"
  | "footer_banner"
  | "mobile_top"
  | "mobile_in_article"
  | "mobile_between_cards"
  | "mobile_sticky_bottom"
  | "hero_grid_bottom"
  | "mobile_above_authors"
  | "mobile_after_hero"
  | "mobile_between_sections"
  | "mobile_between_cards_cat"
  | "mobile_before_footer"
  | "mobile_article_mid"
  | "mobile_between_cards_home"
  | "mobile_search_top"
  | "mobile_search_between"
  | "mobile_search_footer";

export interface AdPlacement {
  id: string;
  name: string;
  position: AdPosition;
  adType: AdType;
  device: AdDevice;
  pageType: AdPageType;
  isActive: boolean;
  adsensePublisherId?: string;
  adsenseSlotId?: string;
  customHtml?: string;
  imageUrl?: string;
  imageAlt?: string;
  linkUrl?: string;
  width: number;
  height: number;
  impressions: number;
  clicks: number;
  title: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdSlot {
  id: string;
  name: string;
  position: string;
  code: string;
  isActive: boolean;
  impressions: number;
  clicks: number;
}

export interface StaticPage {
  id: string;
  title: string;
  slug: string;
  content: string;
  metaTitle: string;
  metaDescription: string;
  isPublished: boolean;
  lastEdited: string;
  sections?: PageSection[];
}

export interface PageSection {
  id: string;
  type: "text" | "heading" | "quote" | "list" | "contact_form" | "image" | "cta";
  content: string;
  title?: string;
}

export interface BreakingNewsItem {
  id: string;
  title: string;
  articleSlug: string;
  isActive: boolean;
  priority: number;
  createdAt: string;
}

export interface SEOSetting {
  key: string;
  value: string;
  description: string;
}

export interface NewsletterSubscriber {
  id: string;
  email: string;
  name: string;
  subscribedAt: string;
  isActive: boolean;
  source: string;
}

export const roleLabels: Record<UserRole, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  editor: "Editor",
  author: "Autor",
  columnist: "Kolumnist",
};

export const rolePermissions: Record<UserRole, string[]> = {
  super_admin: [
    "dashboard",
    "articles",
    "categories",
    "authors",
    "media",
    "ads",
    "homepage",
    "breaking",
    "seo",
    "newsletter",
    "pages",
    "settings",
    "users",
    "analytics",
    "brand",
  ],
  admin: [
    "dashboard",
    "articles",
    "categories",
    "authors",
    "media",
    "ads",
    "homepage",
    "breaking",
    "seo",
    "newsletter",
    "pages",
    "settings",
    "users",
    "analytics",
    "brand",
  ],
  editor: [
    "dashboard",
    "articles",
    "categories",
    "authors",
    "media",
    "breaking",
    "newsletter",
    "pages",
    "seo",
  ],
  author: [
    "dashboard",
    "articles",
    "media",
  ],
  columnist: [
    "dashboard",
    "articles",
    "media",
  ],
};

export const adminUsers: AdminUser[] = [
  {
    id: "1",
    name: "Admin User",
    email: "admin@helvetik.ch",
    role: "super_admin",
    avatar: "",
    isActive: true,
    lastLogin: "2026-04-27T10:00:00Z",
    articleCount: 0,
  },
  {
    id: "2",
    name: "Markus Weber",
    email: "markus@helvetik.ch",
    role: "editor",
    avatar: "",
    isActive: true,
    lastLogin: "2026-04-26T14:30:00Z",
    articleCount: 142,
  },
  {
    id: "3",
    name: "Sarah Müller",
    email: "sarah@helvetik.ch",
    role: "author",
    avatar: "",
    isActive: true,
    lastLogin: "2026-04-25T09:15:00Z",
    articleCount: 78,
  },
  {
    id: "4",
    name: "Claudia Brunner",
    email: "claudia@helvetik.ch",
    role: "columnist",
    avatar: "",
    isActive: true,
    lastLogin: "2026-04-24T16:00:00Z",
    articleCount: 245,
  },
];

export const adminCategories: Category[] = [
  {
    id: "1", name: "Schweiz", slug: "schweiz",
    description: "Aktuelle Nachrichten aus der Schweiz. Politik, Wirtschaft, Gesellschaft und Kultur im Überblick.",
    color: "#8B0000",
    imageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20Swiss%20landscape%20with%20Alps%20mountains%20and%20green%20meadows%20Zurich%20cityscape%20aerial%20view%20professional%20photography%20golden%20hour%20dramatic%20sky%20wide%20angle&width=1200&height=400&seq=cat1&orientation=landscape",
    articleCount: 5, isActive: true, order: 1, showInMenu: true, showOnHomepage: true, homepageOrder: 1,
    seoTitle: "Schweiz News – Aktuelle Nachrichten | Helvetik Journal",
    seoDescription: "Alle aktuellen Nachrichten aus der Schweiz: Politik, Wirtschaft, Gesellschaft und Kultur. Täglich aktuell auf Helvetik Journal.",
    subcategories: [
      { id: "1-1", name: "Innenpolitik", slug: "innenpolitik", parentId: "1" },
      { id: "1-2", name: "Wirtschaft", slug: "wirtschaft-schweiz", parentId: "1" },
      { id: "1-3", name: "Tourismus", slug: "tourismus", parentId: "1" },
      { id: "1-4", name: "Recht", slug: "recht", parentId: "1" },
    ],
  },
  {
    id: "2", name: "Finanzen", slug: "finanzen",
    description: "Börsennews, Aktienanalysen, Wirtschaftsberichte und Finanztipps für die Schweiz.",
    color: "#059669",
    imageUrl: "https://readdy.ai/api/search-image?query=Swiss%20financial%20district%20Zurich%20modern%20glass%20buildings%20stock%20market%20trading%20floor%20professional%20photography%20dramatic%20lighting%20wide%20angle&width=1200&height=400&seq=cat2&orientation=landscape",
    articleCount: 4, isActive: true, order: 2, showInMenu: true, showOnHomepage: true, homepageOrder: 2,
    seoTitle: "Finanzen & Börse Schweiz | Helvetik Journal",
    seoDescription: "Aktuelle Finanznachrichten, Börsenberichte und Wirtschaftsanalysen aus der Schweiz. UBS, SNB, SMI und mehr.",
    subcategories: [
      { id: "2-1", name: "Börse", slug: "boerse", parentId: "2" },
      { id: "2-2", name: "Banken", slug: "banken", parentId: "2" },
      { id: "2-3", name: "Währungen", slug: "waehrungen", parentId: "2" },
      { id: "2-4", name: "Anlagen", slug: "anlagen", parentId: "2" },
      { id: "2-5", name: "Krypto", slug: "krypto", parentId: "2" },
    ],
  },
  {
    id: "3", name: "Versicherung & Gesundheit", slug: "versicherung-gesundheit",
    description: "News zu Krankenkassen, Prämien, Gesundheitspolitik und medizinischen Innovationen.",
    color: "#0d9488",
    imageUrl: "https://readdy.ai/api/search-image?query=Swiss%20healthcare%20hospital%20modern%20medical%20facility%20clean%20white%20interior%20professional%20photography%20soft%20lighting%20wide%20angle&width=1200&height=400&seq=cat3&orientation=landscape",
    articleCount: 3, isActive: true, order: 3, showInMenu: true, showOnHomepage: true, homepageOrder: 3,
    seoTitle: "Versicherung & Gesundheit Schweiz | Helvetik Journal",
    seoDescription: "Krankenkassen, Prämien, Gesundheitspolitik und medizinische Innovationen. Alles rund um Gesundheit in der Schweiz.",
    subcategories: [
      { id: "3-1", name: "Krankenkasse", slug: "krankenkasse", parentId: "3" },
      { id: "3-2", name: "Medizin", slug: "medizin", parentId: "3" },
      { id: "3-3", name: "Pharma", slug: "pharma", parentId: "3" },
      { id: "3-4", name: "Impfung", slug: "impfung", parentId: "3" },
    ],
  },
  {
    id: "4", name: "Technologie", slug: "technologie",
    description: "Die neuesten Entwicklungen in Technologie, Software und Digitalisierung.",
    color: "#0369a1",
    imageUrl: "https://readdy.ai/api/search-image?query=Modern%20technology%20innovation%20lab%20Switzerland%20ETH%20Zurich%20research%20facility%20futuristic%20equipment%20professional%20photography%20dramatic%20lighting%20wide%20angle&width=1200&height=400&seq=cat4&orientation=landscape",
    articleCount: 3, isActive: true, order: 4, showInMenu: true, showOnHomepage: true, homepageOrder: 4,
    seoTitle: "Technologie News Schweiz | Helvetik Journal",
    seoDescription: "Aktuelle Technologie-News aus der Schweiz: Startups, Innovation, Software und Hardware. Täglich aktuell.",
    subcategories: [
      { id: "4-1", name: "Startups", slug: "startups", parentId: "4" },
      { id: "4-2", name: "Hardware", slug: "hardware", parentId: "4" },
      { id: "4-3", name: "Telekommunikation", slug: "telekommunikation", parentId: "4" },
      { id: "4-4", name: "Geschichte", slug: "tech-geschichte", parentId: "4" },
    ],
  },
  {
    id: "5", name: "Digital & KI", slug: "digital-ki",
    description: "Künstliche Intelligenz, Machine Learning und digitale Transformation in der Schweiz.",
    color: "#7c3aed",
    imageUrl: "https://readdy.ai/api/search-image?query=Artificial%20intelligence%20neural%20network%20visualization%20abstract%20digital%20technology%20concept%20professional%20photography%20dark%20background%20dramatic%20lighting&width=1200&height=400&seq=cat5&orientation=landscape",
    articleCount: 3, isActive: true, order: 5, showInMenu: true, showOnHomepage: true, homepageOrder: 5,
    seoTitle: "Digital & KI News Schweiz | Helvetik Journal",
    seoDescription: "Künstliche Intelligenz, Machine Learning und digitale Transformation. Die neuesten KI-News aus der Schweiz.",
    subcategories: [
      { id: "5-1", name: "Künstliche Intelligenz", slug: "kuenstliche-intelligenz", parentId: "5" },
      { id: "5-2", name: "Digitalisierung", slug: "digitalisierung", parentId: "5" },
      { id: "5-3", name: "Cybersecurity", slug: "cybersecurity", parentId: "5" },
    ],
  },
  {
    id: "6", name: "Arbeit & Leben", slug: "arbeit-leben",
    description: "Arbeitsmarkt, Work-Life-Balance, Karriere und Lifestyle in der Schweiz.",
    color: "#d97706",
    imageUrl: "https://readdy.ai/api/search-image?query=Modern%20Swiss%20workplace%20office%20people%20working%20collaboration%20professional%20photography%20warm%20lighting%20wide%20angle&width=1200&height=400&seq=cat6&orientation=landscape",
    articleCount: 2, isActive: true, order: 6, showInMenu: true, showOnHomepage: true, homepageOrder: 6,
    seoTitle: "Arbeit & Leben Schweiz | Helvetik Journal",
    seoDescription: "Arbeitsmarkt, Karriere, Work-Life-Balance und Lifestyle in der Schweiz. Aktuelle News und Ratgeber.",
    subcategories: [
      { id: "6-1", name: "Arbeitsmarkt", slug: "arbeitsmarkt", parentId: "6" },
      { id: "6-2", name: "Karriere", slug: "karriere", parentId: "6" },
      { id: "6-3", name: "Zukunft", slug: "zukunft-arbeit", parentId: "6" },
    ],
  },
  {
    id: "7", name: "Politik", slug: "politik",
    description: "Politische News aus Bern, Wahlen, Abstimmungen und Analysen aus der Schweiz.",
    color: "#475569",
    imageUrl: "https://readdy.ai/api/search-image?query=Swiss%20Federal%20Palace%20Bern%20parliament%20building%20grand%20architecture%20professional%20photography%20dramatic%20sky%20wide%20angle&width=1200&height=400&seq=cat7&orientation=landscape",
    articleCount: 3, isActive: true, order: 7, showInMenu: true, showOnHomepage: true, homepageOrder: 7,
    seoTitle: "Politik Schweiz – Nachrichten & Analysen | Helvetik Journal",
    seoDescription: "Politische Nachrichten aus Bern: Bundesrat, Nationalrat, Abstimmungen und Wahlen. Täglich aktuell.",
    subcategories: [
      { id: "7-1", name: "Bundesrat", slug: "bundesrat", parentId: "7" },
      { id: "7-2", name: "Sozialpolitik", slug: "sozialpolitik", parentId: "7" },
      { id: "7-3", name: "Umweltpolitik", slug: "umweltpolitik", parentId: "7" },
      { id: "7-4", name: "Energiepolitik", slug: "energiepolitik", parentId: "7" },
    ],
  },
  {
    id: "8", name: "Wirtschaft", slug: "wirtschaft",
    description: "Schweizer Wirtschaftsnachrichten, Unternehmensberichte und Marktanalysen.",
    color: "#0f766e",
    imageUrl: "https://readdy.ai/api/search-image?query=Swiss%20economy%20business%20corporate%20headquarters%20modern%20architecture%20professional%20photography%20golden%20hour%20wide%20angle&width=1200&height=400&seq=cat8&orientation=landscape",
    articleCount: 0, isActive: true, order: 8, showInMenu: true, showOnHomepage: false, homepageOrder: 8,
    seoTitle: "Wirtschaft Schweiz – News & Analysen | Helvetik Journal",
    seoDescription: "Schweizer Wirtschaftsnachrichten: Unternehmen, Märkte, Konjunktur und Wirtschaftspolitik.",
    subcategories: [
      { id: "8-1", name: "Unternehmen", slug: "unternehmen", parentId: "8" },
      { id: "8-2", name: "Konjunktur", slug: "konjunktur", parentId: "8" },
      { id: "8-3", name: "Handel", slug: "handel", parentId: "8" },
    ],
  },
  {
    id: "9", name: "Migration & Integration", slug: "migration-integration",
    description: "Einwanderung, Asylpolitik, Integration und gesellschaftlicher Wandel in der Schweiz.",
    color: "#be185d",
    imageUrl: "https://readdy.ai/api/search-image?query=Diverse%20multicultural%20people%20Switzerland%20integration%20community%20professional%20photography%20warm%20lighting%20wide%20angle&width=1200&height=400&seq=cat9&orientation=landscape",
    articleCount: 0, isActive: true, order: 9, showInMenu: false, showOnHomepage: false, homepageOrder: 9,
    seoTitle: "Migration & Integration Schweiz | Helvetik Journal",
    seoDescription: "Nachrichten zu Migration, Asylpolitik und Integration in der Schweiz. Hintergründe und Analysen.",
    subcategories: [
      { id: "9-1", name: "Asylpolitik", slug: "asylpolitik", parentId: "9" },
      { id: "9-2", name: "Integration", slug: "integration", parentId: "9" },
    ],
  },
  {
    id: "10", name: "Bildung", slug: "bildung",
    description: "Bildungspolitik, Schulen, Universitäten und Forschung in der Schweiz.",
    color: "#0891b2",
    imageUrl: "https://readdy.ai/api/search-image?query=ETH%20Zurich%20university%20campus%20modern%20architecture%20students%20learning%20professional%20photography%20bright%20daylight%20wide%20angle&width=1200&height=400&seq=cat10&orientation=landscape",
    articleCount: 0, isActive: true, order: 10, showInMenu: false, showOnHomepage: false, homepageOrder: 10,
    seoTitle: "Bildung Schweiz – News & Analysen | Helvetik Journal",
    seoDescription: "Bildungsnachrichten aus der Schweiz: Schulen, Universitäten, ETH und Bildungspolitik.",
    subcategories: [
      { id: "10-1", name: "Universitäten", slug: "universitaeten", parentId: "10" },
      { id: "10-2", name: "Berufsbildung", slug: "berufsbildung", parentId: "10" },
      { id: "10-3", name: "Forschung", slug: "forschung", parentId: "10" },
    ],
  },
  {
    id: "11", name: "Deals & Angebote", slug: "deals-angebote",
    description: "Die besten Angebote, Deals und Spartipps für die Schweiz.",
    color: "#ea580c",
    imageUrl: "https://readdy.ai/api/search-image?query=Swiss%20shopping%20deals%20sale%20concept%20modern%20retail%20store%20colorful%20products%20professional%20photography%20bright%20lighting%20wide%20angle&width=1200&height=400&seq=cat11&orientation=landscape",
    articleCount: 2, isActive: true, order: 11, showInMenu: true, showOnHomepage: true, homepageOrder: 11,
    seoTitle: "Deals & Angebote Schweiz | Helvetik Journal",
    seoDescription: "Die besten Deals, Angebote und Spartipps aus der Schweiz. Migros, Coop, Digitec und mehr.",
    subcategories: [
      { id: "11-1", name: "Supermärkte", slug: "supermaerkte", parentId: "11" },
      { id: "11-2", name: "Elektronik", slug: "elektronik-deals", parentId: "11" },
      { id: "11-3", name: "Black Friday", slug: "black-friday", parentId: "11" },
    ],
  },
  {
    id: "12", name: "Meinung", slug: "meinung",
    description: "Kommentare, Analysen und Meinungsbeiträge zu aktuellen Themen.",
    color: "#8B0000",
    imageUrl: "https://readdy.ai/api/search-image?query=Opinion%20editorial%20concept%20journalist%20writing%20newspaper%20abstract%20professional%20photography%20dramatic%20lighting%20dark%20background&width=1200&height=400&seq=cat12&orientation=landscape",
    articleCount: 1, isActive: true, order: 12, showInMenu: true, showOnHomepage: true, homepageOrder: 12,
    seoTitle: "Meinung & Kommentar | Helvetik Journal",
    seoDescription: "Meinungsbeiträge, Kommentare und Analysen zu aktuellen Themen aus der Schweiz und der Welt.",
    subcategories: [
      { id: "12-1", name: "Kommentar", slug: "kommentar", parentId: "12" },
      { id: "12-2", name: "Analyse", slug: "analyse", parentId: "12" },
    ],
  },
  {
    id: "13", name: "Kolumnen", slug: "kolumnen",
    description: "Wöchentliche Kolumnen unserer erfahrenen Autoren.",
    color: "#9f1239",
    imageUrl: "https://readdy.ai/api/search-image?query=Elegant%20columnist%20writer%20desk%20with%20pen%20and%20paper%20books%20editorial%20atmosphere%20professional%20photography%20warm%20lighting&width=1200&height=400&seq=cat13&orientation=landscape",
    articleCount: 2, isActive: true, order: 13, showInMenu: true, showOnHomepage: true, homepageOrder: 13,
    seoTitle: "Kolumnen | Helvetik Journal",
    seoDescription: "Wöchentliche Kolumnen unserer erfahrenen Autoren. Tiefgründige Analysen und persönliche Perspektiven.",
    subcategories: [
      { id: "13-1", name: "Brunners Blick", slug: "brunners-blick", parentId: "13" },
      { id: "13-2", name: "Langs Logik", slug: "langs-logik", parentId: "13" },
    ],
  },
];

export const mediaItems: MediaItem[] = [
  { id: "1", name: "hero-klima.jpg", url: "https://readdy.ai/api/search-image?query=Swiss%20Federal%20Council%20meeting%20room%20with%20politicians%20discussing%20climate%20policy%20documents%20on%20wooden%20table%20modern%20government%20building%20Bern%20Switzerland%20professional%20photography%20warm%20lighting%20dramatic%20composition&width=1200&height=675&seq=1&orientation=landscape", type: "image", size: "245 KB", uploadedAt: "2026-04-27T08:00:00Z", dimensions: "1200x675" },
  { id: "2", name: "telekom-tower.jpg", url: "https://readdy.ai/api/search-image?query=Modern%20Swiss%20telecommunications%20tower%20with%205G%20antennas%20against%20Alpine%20mountain%20backdrop%20blue%20sky%20technology%20infrastructure%20professional%20photography%20dramatic%20wide%20angle&width=1200&height=675&seq=2&orientation=landscape", type: "image", size: "198 KB", uploadedAt: "2026-04-26T14:00:00Z", dimensions: "1200x675" },
  { id: "3", name: "health-cross.jpg", url: "https://readdy.ai/api/search-image?query=Swiss%20health%20insurance%20concept%20medical%20cross%20symbol%20clean%20minimalist%20white%20background%20professional%20healthcare%20photography%20soft%20lighting%20elegant%20composition&width=1200&height=675&seq=3&orientation=landscape", type: "image", size: "156 KB", uploadedAt: "2026-04-26T10:00:00Z", dimensions: "1200x675" },
  { id: "4", name: "ubs-building.jpg", url: "https://readdy.ai/api/search-image?query=UBS%20bank%20headquarters%20Zurich%20Switzerland%20modern%20glass%20building%20financial%20district%20professional%20architecture%20photography%20golden%20hour%20lighting%20dramatic%20sky&width=1200&height=675&seq=4&orientation=landscape", type: "image", size: "312 KB", uploadedAt: "2026-04-25T16:00:00Z", dimensions: "1200x675" },
  { id: "5", name: "google-campus.jpg", url: "https://readdy.ai/api/search-image?query=Modern%20Google%20office%20building%20Zurich%20Switzerland%20contemporary%20architecture%20glass%20facade%20tech%20campus%20professional%20photography%20daylight%20wide%20angle&width=1200&height=675&seq=5&orientation=landscape", type: "image", size: "278 KB", uploadedAt: "2026-04-25T09:00:00Z", dimensions: "1200x675" },
  { id: "6", name: "parliament.jpg", url: "https://readdy.ai/api/search-image?query=Swiss%20National%20Council%20parliament%20session%20Bern%20politicians%20debating%20grand%20assembly%20hall%20red%20seats%20professional%20photography%20documentary%20style%20dramatic%20lighting&width=1200&height=675&seq=6&orientation=landscape", type: "image", size: "189 KB", uploadedAt: "2026-04-24T11:00:00Z", dimensions: "1200x675" },
];

export const adSlots: AdSlot[] = [
  { id: "1", name: "Header Banner", position: "header", code: "<ins class=\"adsbygoogle\" style=\"display:block\" data-ad-client=\"ca-pub-XXXX\" data-ad-slot=\"header\" data-ad-format=\"auto\"></ins>", isActive: true, impressions: 45230, clicks: 312 },
  { id: "2", name: "Sidebar Top", position: "sidebar-top", code: "<ins class=\"adsbygoogle\" style=\"display:block\" data-ad-client=\"ca-pub-XXXX\" data-ad-slot=\"sidebar-top\" data-ad-format=\"auto\"></ins>", isActive: true, impressions: 38900, clicks: 245 },
  { id: "3", name: "Article Inline", position: "article-inline", code: "<ins class=\"adsbygoogle\" style=\"display:block\" data-ad-client=\"ca-pub-XXXX\" data-ad-slot=\"article-inline\" data-ad-format=\"auto\"></ins>", isActive: true, impressions: 52100, clicks: 478 },
  { id: "4", name: "Footer Banner", position: "footer", code: "<ins class=\"adsbygoogle\" style=\"display:block\" data-ad-client=\"ca-pub-XXXX\" data-ad-slot=\"footer\" data-ad-format=\"auto\"></ins>", isActive: false, impressions: 0, clicks: 0 },
];

export const adPlacements: AdPlacement[] = [
  {
    id: "ad-1",
    name: "Header Banner Desktop",
    position: "header_banner",
    adType: "adsense",
    device: "desktop",
    pageType: "all",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "1234567890",
    width: 970,
    height: 250,
    impressions: 124500,
    clicks: 1035,
    title: "Header Banner Desktop",
    description: "Hauptbanner oben auf allen Seiten (Desktop)",
    createdAt: "2026-01-15T10:00:00Z",
    updatedAt: "2026-04-20T08:00:00Z",
  },
  {
    id: "ad-2",
    name: "Top Leaderboard",
    position: "top_leaderboard",
    adType: "adsense",
    device: "desktop",
    pageType: "all",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "2345678901",
    width: 728,
    height: 90,
    impressions: 98200,
    clicks: 812,
    title: "Top Leaderboard",
    description: "Leaderboard unter der Navigation",
    createdAt: "2026-01-15T10:00:00Z",
    updatedAt: "2026-04-20T08:00:00Z",
  },
  {
    id: "ad-3",
    name: "Sidebar Rectangle",
    position: "sidebar_top",
    adType: "adsense",
    device: "desktop",
    pageType: "article",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "3456789012",
    width: 300,
    height: 250,
    impressions: 87400,
    clicks: 623,
    title: "Sidebar Rectangle",
    description: "Sidebar-Werbung oben (300x250)",
    createdAt: "2026-01-15T10:00:00Z",
    updatedAt: "2026-04-22T10:00:00Z",
  },
  {
    id: "ad-4",
    name: "Sidebar Large",
    position: "sidebar_large",
    adType: "image_banner",
    device: "desktop",
    pageType: "article",
    isActive: true,
    imageUrl: "https://readdy.ai/api/search-image?query=Swiss%20bank%20investment%20advertisement%20elegant%20minimal%20design%20dark%20background%20gold%20text%20professional%20financial%20services%20banner&width=300&height=600&seq=ad4&orientation=portrait",
    imageAlt: "Helvetik Finance Partner",
    linkUrl: "https://example.ch/partner",
    width: 300,
    height: 600,
    impressions: 54300,
    clicks: 389,
    title: "Sidebar Large (Half Page)",
    description: "Grosses Sidebar-Banner (300x600)",
    createdAt: "2026-02-01T10:00:00Z",
    updatedAt: "2026-04-18T14:00:00Z",
  },
  {
    id: "ad-5",
    name: "In-Article nach Absatz 2",
    position: "in_article_p2",
    adType: "adsense",
    device: "desktop",
    pageType: "article",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "4567890123",
    width: 728,
    height: 90,
    impressions: 112800,
    clicks: 1240,
    title: "In-Article Ad (nach Absatz 2)",
    description: "Werbung innerhalb des Artikels nach dem 2. Absatz",
    createdAt: "2026-01-20T10:00:00Z",
    updatedAt: "2026-04-25T09:00:00Z",
  },
  {
    id: "ad-6",
    name: "In-Article nach Absatz 5",
    position: "in_article_p5",
    adType: "adsense",
    device: "desktop",
    pageType: "article",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "5678901234",
    width: 728,
    height: 90,
    impressions: 89600,
    clicks: 934,
    title: "In-Article Ad (nach Absatz 5)",
    description: "Werbung innerhalb des Artikels nach dem 5. Absatz",
    createdAt: "2026-01-20T10:00:00Z",
    updatedAt: "2026-04-25T09:00:00Z",
  },
  {
    id: "ad-7",
    name: "Homepage Section Ad",
    position: "homepage_section",
    adType: "custom_html",
    device: "all",
    pageType: "homepage",
    isActive: true,
    customHtml: "<div class=\"partner-ad\"><a href=\"https://example.ch\"><img src=\"https://via.placeholder.com/728x90\" alt=\"Partner\"/></a></div>",
    width: 728,
    height: 90,
    impressions: 67200,
    clicks: 445,
    title: "Homepage Section Banner",
    description: "Werbebanner zwischen Homepage-Sektionen",
    createdAt: "2026-02-10T10:00:00Z",
    updatedAt: "2026-04-10T12:00:00Z",
  },
  {
    id: "ad-8",
    name: "Category Page Ad",
    position: "category_page",
    adType: "adsense",
    device: "desktop",
    pageType: "category",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "6789012345",
    width: 300,
    height: 250,
    impressions: 43100,
    clicks: 287,
    title: "Category Page Sidebar Ad",
    description: "Sidebar-Werbung auf Kategorieseiten",
    createdAt: "2026-02-15T10:00:00Z",
    updatedAt: "2026-04-15T10:00:00Z",
  },
  {
    id: "ad-9",
    name: "Article Bottom Ad",
    position: "article_bottom",
    adType: "adsense",
    device: "all",
    pageType: "article",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "7890123456",
    width: 728,
    height: 90,
    impressions: 78900,
    clicks: 698,
    title: "Article Bottom Leaderboard",
    description: "Werbung am Ende des Artikels",
    createdAt: "2026-01-25T10:00:00Z",
    updatedAt: "2026-04-20T10:00:00Z",
  },
  {
    id: "ad-10",
    name: "Footer Banner",
    position: "footer_banner",
    adType: "image_banner",
    device: "desktop",
    pageType: "all",
    isActive: false,
    imageUrl: "https://readdy.ai/api/search-image?query=Swiss%20technology%20company%20advertisement%20clean%20minimal%20design%20white%20background%20professional%20corporate%20banner%20wide%20format&width=970&height=250&seq=ad10&orientation=landscape",
    imageAlt: "Helvetik Tech Partner",
    linkUrl: "https://example.ch/tech-partner",
    width: 970,
    height: 250,
    impressions: 0,
    clicks: 0,
    title: "Footer Banner Desktop",
    description: "Grosses Banner im Footer-Bereich",
    createdAt: "2026-03-01T10:00:00Z",
    updatedAt: "2026-03-01T10:00:00Z",
  },
  {
    id: "ad-11",
    name: "Mobile Top Banner",
    position: "mobile_top",
    adType: "adsense",
    device: "mobile",
    pageType: "all",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "8901234567",
    width: 320,
    height: 100,
    impressions: 156700,
    clicks: 1890,
    title: "Mobile Top Banner",
    description: "Banner oben auf mobilen Geräten (320x100)",
    createdAt: "2026-01-15T10:00:00Z",
    updatedAt: "2026-04-22T08:00:00Z",
  },
  {
    id: "ad-12",
    name: "Mobile In-Article",
    position: "mobile_in_article",
    adType: "adsense",
    device: "mobile",
    pageType: "article",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "9012345678",
    width: 320,
    height: 250,
    impressions: 134200,
    clicks: 1567,
    title: "Mobile In-Article Ad",
    description: "Werbung innerhalb von Artikeln auf Mobilgeräten",
    createdAt: "2026-01-20T10:00:00Z",
    updatedAt: "2026-04-22T08:00:00Z",
  },
  {
    id: "ad-13",
    name: "Mobile Between Cards",
    position: "mobile_between_cards",
    adType: "adsense",
    device: "mobile",
    pageType: "homepage",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "0123456789",
    width: 320,
    height: 100,
    impressions: 98400,
    clicks: 1123,
    title: "Mobile Between Cards",
    description: "Werbung zwischen Artikelkarten auf Mobilgeräten",
    createdAt: "2026-02-01T10:00:00Z",
    updatedAt: "2026-04-18T10:00:00Z",
  },
  {
    id: "ad-14",
    name: "Mobile Sticky Bottom",
    position: "mobile_sticky_bottom",
    adType: "adsense",
    device: "mobile",
    pageType: "all",
    isActive: false,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "1122334455",
    width: 320,
    height: 50,
    impressions: 0,
    clicks: 0,
    title: "Mobile Sticky Bottom Banner",
    description: "Klebendes Banner am unteren Bildschirmrand (optional)",
    createdAt: "2026-03-15T10:00:00Z",
    updatedAt: "2026-03-15T10:00:00Z",
  },
  {
    id: "ad-15",
    name: "Hero Grid Altı Leaderboard",
    position: "hero_grid_bottom",
    adType: "adsense",
    device: "all",
    pageType: "homepage",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "2233445566",
    width: 728,
    height: 90,
    impressions: 65400,
    clicks: 512,
    title: "Hero Grid Altı Leaderboard",
    description: "İnce uzun reklam alanı, 5'li kartın altında, yazarlar bölümünün üstünde",
    createdAt: "2026-04-01T10:00:00Z",
    updatedAt: "2026-05-07T10:00:00Z",
  },
  {
    id: "ad-16",
    name: "Mobile Hero Ad",
    position: "mobile_after_hero",
    adType: "adsense",
    device: "mobile",
    pageType: "all",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "3344556677",
    width: 320,
    height: 100,
    impressions: 98700,
    clicks: 876,
    title: "Mobile Hero Altı Ad",
    description: "Mobil hero alanının altındaki reklam alanı (320x100)",
    createdAt: "2026-04-01T10:00:00Z",
    updatedAt: "2026-05-08T10:00:00Z",
  },
  {
    id: "ad-17",
    name: "Mobile über Autoren",
    position: "mobile_above_authors",
    adType: "adsense",
    device: "mobile",
    pageType: "all",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "4455667788",
    width: 320,
    height: 50,
    impressions: 112300,
    clicks: 945,
    title: "Mobile über Autoren Ad",
    description: "Yazarlar bölümünün üstünde, mobilde görünen reklam alanı (320x50)",
    createdAt: "2026-04-01T10:00:00Z",
    updatedAt: "2026-05-08T10:00:00Z",
  },
  {
    id: "ad-18",
    name: "Mobile zwischen Sektionen",
    position: "mobile_between_sections",
    adType: "adsense",
    device: "mobile",
    pageType: "homepage",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "5566778899",
    width: 320,
    height: 100,
    impressions: 76500,
    clicks: 623,
    title: "Mobile Sektionen Arası Ad",
    description: "Mobil ana sayfada kategori sektörleri arasındaki reklam alanı (320x100)",
    createdAt: "2026-04-01T10:00:00Z",
    updatedAt: "2026-05-08T10:00:00Z",
  },
  {
    id: "ad-19",
    name: "Mobile Category Cards Arası",
    position: "mobile_between_cards_cat",
    adType: "adsense",
    device: "mobile",
    pageType: "category",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "6677889900",
    width: 320,
    height: 100,
    impressions: 54300,
    clicks: 412,
    title: "Mobile Category Cards Arası Ad",
    description: "Kategori sayfasında mobil kartlar arası reklam alanı (320x100)",
    createdAt: "2026-05-01T10:00:00Z",
    updatedAt: "2026-05-10T10:00:00Z",
  },
  {
    id: "ad-20",
    name: "Mobile Footer Öncesi",
    position: "mobile_before_footer",
    adType: "adsense",
    device: "mobile",
    pageType: "all",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "7788990011",
    width: 320,
    height: 100,
    impressions: 67800,
    clicks: 534,
    title: "Mobile Footer Öncesi Ad",
    description: "Mobil footer öncesi reklam alanı (320x100)",
    createdAt: "2026-05-01T10:00:00Z",
    updatedAt: "2026-05-10T10:00:00Z",
  },
  {
    id: "ad-21",
    name: "Mobile Article Mid",
    position: "mobile_article_mid",
    adType: "adsense",
    device: "mobile",
    pageType: "article",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "8899001122",
    width: 320,
    height: 250,
    impressions: 82300,
    clicks: 756,
    title: "Mobile Article Mid Ad",
    description: "Mobil makale ortası reklam alanı (320x250)",
    createdAt: "2026-05-01T10:00:00Z",
    updatedAt: "2026-05-10T10:00:00Z",
  },
  {
    id: "ad-22",
    name: "Mobile Homepage Cards Arası",
    position: "mobile_between_cards_home",
    adType: "adsense",
    device: "mobile",
    pageType: "homepage",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "9900112233",
    width: 320,
    height: 100,
    impressions: 45600,
    clicks: 387,
    title: "Mobile Homepage Cards Arası Ad",
    description: "Mobil ana sayfada kartlar arası reklam alanı (320x100)",
    createdAt: "2026-05-01T10:00:00Z",
    updatedAt: "2026-05-10T10:00:00Z",
  },
  {
    id: "ad-23",
    name: "Mobile Search Top",
    position: "mobile_search_top",
    adType: "adsense",
    device: "mobile",
    pageType: "search",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "0011223344",
    width: 320,
    height: 100,
    impressions: 32100,
    clicks: 256,
    title: "Mobile Search Top Ad",
    description: "Mobil arama sonuçları üstündeki reklam alanı (320x100)",
    createdAt: "2026-05-01T10:00:00Z",
    updatedAt: "2026-05-10T10:00:00Z",
  },
  {
    id: "ad-24",
    name: "Mobile Search Between",
    position: "mobile_search_between",
    adType: "adsense",
    device: "mobile",
    pageType: "search",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "1122334455",
    width: 320,
    height: 100,
    impressions: 28900,
    clicks: 198,
    title: "Mobile Search Between Ad",
    description: "Mobil arama sonuçları arası reklam alanı (320x100)",
    createdAt: "2026-05-01T10:00:00Z",
    updatedAt: "2026-05-10T10:00:00Z",
  },
  {
    id: "ad-25",
    name: "Mobile Search Footer",
    position: "mobile_search_footer",
    adType: "adsense",
    device: "mobile",
    pageType: "search",
    isActive: true,
    adsensePublisherId: "ca-pub-XXXXXXXXXX",
    adsenseSlotId: "2233445566",
    width: 320,
    height: 100,
    impressions: 34500,
    clicks: 267,
    title: "Mobile Search Footer Ad",
    description: "Mobil arama sonuçları altındaki reklam alanı (320x100)",
    createdAt: "2026-05-01T10:00:00Z",
    updatedAt: "2026-05-10T10:00:00Z",
  },
];

export const staticPages: StaticPage[] = [
  {
    id: "1",
    title: "Kontakt",
    slug: "kontakt",
    content: "Haben Sie Fragen, Anregungen oder möchten Sie uns einen Hinweis geben? Wir freuen uns auf Ihre Nachricht. Unser Redaktionsteam ist für Sie da und beantwortet Ihre Anfragen so schnell wie möglich.",
    metaTitle: "Kontakt – Helvetik Journal",
    metaDescription: "Kontaktieren Sie das Helvetik Journal Redaktionsteam. Wir freuen uns auf Ihre Anfragen, Hinweise und Feedback.",
    isPublished: true,
    lastEdited: "2026-04-28T10:00:00Z",
    sections: [
      { id: "k1", type: "heading", content: "Redaktion" },
      { id: "k2", type: "text", content: "Für redaktionelle Anfragen, Pressemitteilungen und Hinweise erreichen Sie uns unter redaktion@helvetik-journal.ch." },
      { id: "k3", type: "heading", content: "Werbung & Kooperationen" },
      { id: "k4", type: "text", content: "Interessiert an einer Partnerschaft oder Werbeschaltung? Besuchen Sie unsere Media-Kit-Seite oder schreiben Sie uns direkt an werbung@helvetik-journal.ch." },
      { id: "k5", type: "heading", content: "Technischer Support" },
      { id: "k6", type: "text", content: "Bei technischen Problemen mit der Website wenden Sie sich bitte an support@helvetik-journal.ch." },
    ],
  },
  {
    id: "2",
    title: "Über uns",
    slug: "ueber-uns",
    content: "Helvetik Journal ist eine unabhängige Schweizer Online-Nachrichtenplattform, die sich der qualitativ hochwertigen Berichterstattung über die Themen verschiedenster Bereiche widmet. Seit unserer Gründung im Jahr 2020 stehen wir für unabhängigen Journalismus, faktenbasierte Analyse und eine klare Sprache, die komplexe Zusammenhänge verständlich macht.",
    metaTitle: "Über uns – Helvetik Journal",
    metaDescription: "Erfahren Sie mehr über Helvetik Journal, Ihre unabhängige Schweizer Nachrichtenplattform für News, Finanzen, Technologie und mehr.",
    isPublished: true,
    lastEdited: "2026-04-28T10:00:00Z",
    sections: [
      { id: "u1", type: "heading", content: "Unsere Mission" },
      { id: "u2", type: "text", content: "Wir informieren die Schweiz über das, was wirklich zählt. Von der Bundesratswahl bis zur Krankenkassenprämie, von der Börse bis zur KI-Revolution – wir liefern Ihnen die Hintergründe, die Sie für fundierte Entscheidungen brauchen." },
      { id: "u3", type: "heading", content: "Unabhängiger Journalismus" },
      { id: "u4", type: "text", content: "Unsere Redaktion steht für journalistische Qualität und ethische Standards. Wir lassen uns nicht von politischen oder wirtschaftlichen Interessen leiten. Jeder Artikel durchläuft einen sorgfältigen Fact-Check-Prozess." },
      { id: "u5", type: "heading", content: "Die Schweiz im Fokus" },
      { id: "u6", type: "text", content: "Unser Fokus liegt auf der Schweiz – aber mit internationalem Blick. Wir verstehen die globale Vernetzung und zeigen auf, wie internationale Entwicklungen die Schweiz beeinflussen." },
      { id: "u7", type: "heading", content: "Unsere Themen" },
      { id: "u8", type: "list", content: "Politik & Gesellschaft|Finanzen & Wirtschaft|Versicherung & Gesundheit|Technologie & KI|Arbeit & Leben|Deals & Angebote|Meinung & Kolumnen" },
      { id: "u9", type: "heading", content: "Redaktionelle Leitung" },
      { id: "u10", type: "text", content: "Markus Weber, Chefredakteur\nDr. Anna Fischer, Verlagsleiterin\nThomas Lang, Leitender Redakteur Finanzen\nClaudia Brunner, Kolumnistin und Meinungsredakteurin" },
    ],
  },
  {
    id: "3",
    title: "Impressum",
    slug: "impressum",
    content: "Angaben gemäss den geltenden rechtlichen Bestimmungen der Schweiz.",
    metaTitle: "Impressum – Helvetik Journal",
    metaDescription: "Impressum der Helvetik Journal Website. Rechtliche Angaben gemäss Schweizer Recht.",
    isPublished: true,
    lastEdited: "2026-04-28T10:00:00Z",
    sections: [
      { id: "i1", type: "heading", content: "Angaben gemäss Swiss Digital Federation" },
      { id: "i2", type: "text", content: "Helvetik Journal AG\nBahnhofstrasse 42\n8001 Zürich\nSchweiz" },
      { id: "i3", type: "heading", content: "Kontakt" },
      { id: "i4", type: "text", content: "E-Mail: redaktion@helvetik-journal.ch\nTelefon: +41 44 123 45 67" },
      { id: "i5", type: "heading", content: "Handelsregister" },
      { id: "i6", type: "text", content: "Handelsregister des Kantons Zürich\nCHE-123.456.789" },
      { id: "i7", type: "heading", content: "Vertretungsberechtigte Personen" },
      { id: "i8", type: "text", content: "Markus Weber, Geschäftsführer\nDr. Anna Fischer, Verlagsleiterin" },
      { id: "i9", type: "heading", content: "Haftungsausschluss" },
      { id: "i10", type: "text", content: "Die Inhalte dieser Website wurden mit grösstmöglicher Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäss den geltenden Gesetzen für eigene Inhalte auf diesen Seiten verantwortlich." },
      { id: "i11", type: "heading", content: "Urheberrecht" },
      { id: "i12", type: "text", content: "Die auf dieser Website veröffentlichten Inhalte und Werke unterliegen dem schweizerischen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung ausserhalb der Grenzen des Urheberrechts bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers." },
    ],
  },
  {
    id: "4",
    title: "Datenschutz",
    slug: "datenschutz",
    content: "Datenschutzerklärung gemäss dem Schweizer Datenschutzgesetz (DSG) und der DSGVO.",
    metaTitle: "Datenschutzerklärung – Helvetik Journal",
    metaDescription: "Datenschutzerklärung der Helvetik Journal Website. Ihre Daten sind bei uns sicher.",
    isPublished: true,
    lastEdited: "2026-04-28T10:00:00Z",
    sections: [
      { id: "d1", type: "heading", content: "1. Verantwortliche Stelle" },
      { id: "d2", type: "text", content: "Verantwortlich für die Datenverarbeitung auf dieser Website ist die Helvetik Journal AG, Bahnhofstrasse 42, 8001 Zürich, Schweiz. Bei Fragen zum Datenschutz können Sie uns unter datenschutz@helvetik-journal.ch kontaktieren." },
      { id: "d3", type: "heading", content: "2. Erhebung und Verarbeitung personenbezogener Daten" },
      { id: "d4", type: "text", content: "Wir erheben personenbezogene Daten nur, wenn Sie uns diese freiwillig mitteilen, beispielsweise bei der Kontaktaufnahme oder der Newsletter-Anmeldung. Diese Daten verwenden wir ausschliesslich für den angegebenen Zweck und gemäss den geltenden Datenschutzbestimmungen." },
      { id: "d5", type: "heading", content: "3. Cookies" },
      { id: "d6", type: "text", content: "Unsere Website verwendet Cookies, um die Benutzerfreundlichkeit zu verbessern und statistische Daten zu erheben. Sie können die Verwendung von Cookies in Ihren Browsereinstellungen einschränken oder deaktivieren." },
      { id: "d7", type: "heading", content: "4. Google Analytics" },
      { id: "d8", type: "text", content: "Diese Website nutzt Google Analytics, einen Webanalysedienst der Google LLC. Google Analytics verwendet Cookies, die eine Analyse der Benutzung der Website ermöglichen." },
      { id: "d9", type: "heading", content: "5. Google AdSense" },
      { id: "d10", type: "text", content: "Auf dieser Website werden Anzeigen über Google AdSense geschaltet. Google verwendet Cookies, um Anzeigen auf dieser Website zu schalten und um wiederholte Besuche zu erfassen." },
      { id: "d11", type: "heading", content: "6. Ihre Rechte" },
      { id: "d12", type: "text", content: "Sie haben das Recht, Auskunft über die bei uns gespeicherten personenbezogenen Daten zu erhalten, sowie das Recht auf Berichtigung, Löschung oder Einschränkung der Verarbeitung." },
      { id: "d13", type: "heading", content: "7. Datensicherheit" },
      { id: "d14", type: "text", content: "Wir setzen technische und organisatorische Sicherheitsmassnahmen ein, um Ihre Daten gegen Manipulation, Verlust, Zerstörung oder gegen den Zugriff unberechtigter Personen zu schützen." },
      { id: "d15", type: "heading", content: "8. Änderungen der Datenschutzerklärung" },
      { id: "d16", type: "text", content: "Wir behalten uns vor, diese Datenschutzerklärung bei Bedarf anzupassen, um sie an geänderte rechtliche Rahmenbedingungen oder bei Änderungen des Dienstes sowie der Datenverarbeitung anzupassen." },
    ],
  },
  {
    id: "5",
    title: "Cookie-Richtlinie",
    slug: "cookie-richtlinie",
    content: "Informationen über die Verwendung von Cookies auf unserer Website.",
    metaTitle: "Cookie-Richtlinie – Helvetik Journal",
    metaDescription: "Cookie-Richtlinie der Helvetik Journal Website. Erfahren Sie mehr über unsere Cookie-Praxis.",
    isPublished: true,
    lastEdited: "2026-04-28T10:00:00Z",
    sections: [
      { id: "c1", type: "heading", content: "Was sind Cookies?" },
      { id: "c2", type: "text", content: "Cookies sind kleine Textdateien, die auf Ihrem Computer oder Mobilgerät gespeichert werden, wenn Sie eine Website besuchen. Sie ermöglichen es der Website, Ihre Aktionen und Präferenzen über einen bestimmten Zeitraum zu speichern." },
      { id: "c3", type: "heading", content: "Wie verwenden wir Cookies?" },
      { id: "c4", type: "text", content: "Helvetik Journal verwendet Cookies für verschiedene Zwecke:" },
      { id: "c5", type: "list", content: "Notwendige Cookies: Für den Betrieb der Website unerlässlich|Funktionale Cookies: Ermöglichen erweiterte Funktionen und Personalisierung|Analytische Cookies: Helfen uns zu verstehen, wie Besucher mit der Website interagieren|Werbe-Cookies: Werden verwendet, um Ihnen relevante Werbung anzuzeigen" },
      { id: "c6", type: "heading", content: "Drittanbieter-Cookies" },
      { id: "c7", type: "text", content: "Auf unserer Website werden auch Cookies von Drittanbietern verwendet, insbesondere Google Analytics zur Analyse des Website-Traffics und Google AdSense zur Schaltung personalisierter Werbung." },
      { id: "c8", type: "heading", content: "Cookie-Verwaltung" },
      { id: "c9", type: "text", content: "Sie können die Verwendung von Cookies in Ihren Browsereinstellungen steuern. Die meisten Browser erlauben es Ihnen, Cookies zu akzeptieren, abzulehnen oder zu löschen." },
      { id: "c10", type: "heading", content: "Kontakt" },
      { id: "c11", type: "text", content: "Bei Fragen zu unserer Cookie-Richtlinie kontaktieren Sie uns unter datenschutz@helvetik-journal.ch." },
    ],
  },
  {
    id: "6",
    title: "Werbung & Media Kit",
    slug: "werbung",
    content: "Informationen für Werbepartner und Medienvertreter.",
    metaTitle: "Werbung & Media Kit – Helvetik Journal",
    metaDescription: "Werben Sie auf Helvetik Journal. Erfahren Sie mehr über unsere Reichweite, Zielgruppe und Werbeformate.",
    isPublished: false,
    lastEdited: "2026-04-28T10:00:00Z",
    sections: [
      { id: "w1", type: "heading", content: "Warum Helvetik Journal?" },
      { id: "w2", type: "text", content: "Helvetik Journal ist eine der am schnellsten wachsenden Nachrichtenplattformen der Schweiz. Mit über 500.000 monatlichen Besuchern und einer engagierten Leserschaft bieten wir Werbepartnern eine exzellente Plattform für ihre Botschaften." },
      { id: "w3", type: "heading", content: "Unsere Reichweite" },
      { id: "w4", type: "list", content: "500.000+ monatliche Besucher|2.5 Millionen+ monatliche Seitenaufrufe|180.000 Newsletter-Abonnenten|45% weibliche Leser, 55% männliche Leser|Durchschnittsalter: 35-54 Jahre" },
      { id: "w5", type: "heading", content: "Werbeformate" },
      { id: "w6", type: "text", content: "Wir bieten verschiedene Werbeformate an, die sich nahtlos in unsere redaktionelle Umgebung einfügen:" },
      { id: "w7", type: "list", content: "Display-Werbung: Leaderboard, Skyscraper, Rectangle|Native Advertising: Redaktionell gestaltete Sponsored Content|Newsletter-Werbung: Platzierung in unserem täglichen Newsletter|Video-Pre-Roll: Vor redaktionellen Video-Inhalten|Social Media Promotion: Verbreitung über unsere Kanäle" },
      { id: "w8", type: "heading", content: "Ad-Platzierungen" },
      { id: "w9", type: "text", content: "Unsere Website bietet strategisch platzierte Werbeflächen für maximale Sichtbarkeit: Header-Banner (970x250), Top-Leaderboard (728x90), Sidebar-Rectangle (300x250), Sidebar-Half-Page (300x600), In-Article-Ads und Mobile-Banner (320x100)." },
      { id: "w10", type: "heading", content: "Kontakt" },
      { id: "w11", type: "text", content: "Für Werbeanfragen und Medienpartnerschaften kontaktieren Sie uns unter werbung@helvetik-journal.ch oder nutzen Sie das Kontaktformular auf dieser Seite." },
    ],
  },
];

export const breakingNewsItems: BreakingNewsItem[] = [
  { id: "1", title: "Bundesrat beschließt neue Klimaschutz-Massnahmen für 2026", articleSlug: "bundesrat-klimaschutz-massnahmen-2026", isActive: true, priority: 1, createdAt: "2026-04-27T08:30:00Z" },
  { id: "2", title: "Roche meldet Durchbruch bei Alzheimer-Forschung", articleSlug: "roche-alzheimer-durchbruch", isActive: true, priority: 2, createdAt: "2026-04-23T07:45:00Z" },
  { id: "3", title: "Schweizer Franken erreicht 5-Jahres-Hoch gegenüber dem Euro", articleSlug: "schweizer-franken-5-jahres-hoch", isActive: true, priority: 3, createdAt: "2026-04-22T15:30:00Z" },
  { id: "4", title: "BAG warnt vor neuer Grippewelle im Mai", articleSlug: "bag-grippewelle-mai-2026", isActive: false, priority: 4, createdAt: "2026-04-16T08:00:00Z" },
];

export const seoSettings: SEOSetting[] = [
  { key: "site_title", value: "Helvetik Journal - Die Schweiz im Fokus.", description: "Haupttitel der Website" },
  { key: "site_description", value: "Helvetik Journal - Ihre unabhängige Schweizer Nachrichtenplattform. Aktuelle News zu Politik, Finanzen, Gesundheits-Versicherung, Technologie & KI.", description: "Meta-Beschreibung der Website" },
  { key: "og_image", value: "https://helvetik-journal.ch/og-image.jpg", description: "Open Graph Bild-URL" },
  { key: "twitter_handle", value: "@helvetikjournal", description: "Twitter/X Handle" },
  { key: "google_analytics_id", value: "G-XXXXXXXXXX", description: "Google Analytics Tracking-ID" },
  { key: "google_adsense_id", value: "ca-pub-XXXXXXXXXX", description: "Google AdSense Publisher-ID" },
  { key: "robots_txt", value: "User-agent: *\nDisallow: /admin/\nAllow: /\nSitemap: https://helvetik-journal.ch/sitemap.xml", description: "Robots.txt Inhalt" },
  { key: "sitemap_enabled", value: "true", description: "XML Sitemap aktivieren" },
];

export const newsletterSubscribers: NewsletterSubscriber[] = [
  { id: "1", email: "hans.mueller@example.ch", name: "Hans Müller", subscribedAt: "2026-04-25T10:00:00Z", isActive: true, source: "Website" },
  { id: "2", email: "anna.schmidt@example.ch", name: "Anna Schmidt", subscribedAt: "2026-04-24T14:30:00Z", isActive: true, source: "Footer" },
  { id: "3", email: "peter.weber@example.ch", name: "Peter Weber", subscribedAt: "2026-04-23T09:15:00Z", isActive: true, source: "Popup" },
  { id: "4", email: "lisa.fischer@example.ch", name: "Lisa Fischer", subscribedAt: "2026-04-22T16:00:00Z", isActive: false, source: "Website" },
  { id: "5", email: "markus.keller@example.ch", name: "Markus Keller", subscribedAt: "2026-04-21T11:00:00Z", isActive: true, source: "Footer" },
  { id: "6", email: "sarah.huber@example.ch", name: "Sarah Huber", subscribedAt: "2026-04-20T08:00:00Z", isActive: true, source: "Website" },
  { id: "7", email: "thomas.lang@example.ch", name: "Thomas Lang", subscribedAt: "2026-04-19T13:00:00Z", isActive: true, source: "Popup" },
  { id: "8", email: "claudia.brunner@example.ch", name: "Claudia Brunner", subscribedAt: "2026-04-18T10:00:00Z", isActive: true, source: "Footer" },
];

export const homepageSections = [
  { id: "hero", name: "Hero Slider", isActive: true, order: 1 },
  { id: "top-news", name: "Top News", isActive: true, order: 2 },
  { id: "latest", name: "Neueste Nachrichten", isActive: true, order: 3 },
  { id: "finanzen", name: "Finanzen", isActive: true, order: 4 },
  { id: "gesundheit", name: "Versicherung & Gesundheit", isActive: true, order: 5 },
  { id: "technologie", name: "Technologie & KI", isActive: true, order: 6 },
  { id: "schweiz", name: "Schweiz", isActive: true, order: 7 },
  { id: "arbeit", name: "Arbeit & Leben", isActive: true, order: 8 },
  { id: "deals", name: "Deals & Angebote", isActive: true, order: 9 },
  { id: "meinung", name: "Meinung & Kolumnen", isActive: true, order: 10 },
  { id: "newsletter", name: "Newsletter CTA", isActive: true, order: 11 },
];